import React from "react";
import { Tooltip } from "@chakra-ui/react";
import { useKnowledgeBaseStore } from "../../../hooks/knowledgeBase.hooks";
import AppIcon from "../../Core/AppIcon/AppIcon";
import { useKnowledgeBaseSide } from "./KnowledgeBaseSideContext";

interface KnowledgeBaseTriggerProps {
	tag: string;
	tooltip?: string;
	children?: React.ReactNode;
	margin?: string;
	/**
	 * Edge the sidebar opens from. Defaults to the surrounding
	 * `KnowledgeBaseSideContext` (which is "right" unless a parent — e.g. the
	 * filter sidebar — overrides it to "left").
	 */
	side?: "left" | "right";
}

/**
 * Reusable component that wraps any child component as a trigger to open the knowledge base sidebar.
 * If no children are provided, it renders a default help icon.
 *
 * Usage:
 * <KnowledgeBaseTrigger tag="booking-type" tooltip="Learn more about booking types" />
 * <KnowledgeBaseTrigger tag="booking-type">
 *   <Button>Custom Trigger</Button>
 * </KnowledgeBaseTrigger>
 */
const KnowledgeBaseTrigger: React.FC<KnowledgeBaseTriggerProps> = ({
	tag,
	tooltip = "Läs mer i hjälpcentret",
	children,
	margin,
	side,
}) => {
	const { openKnowledgeBase } = useKnowledgeBaseStore();
	const contextSide = useKnowledgeBaseSide();

	const handleClick = () => {
		openKnowledgeBase([tag], side ?? contextSide);
	};

	const trigger = children || <AppIcon icon="help" size="13px" />;

	return (
		<Tooltip label={tooltip} placement="top">
			<span
				onClick={handleClick}
				style={{
					cursor: "pointer",
					display: "inline-flex",
					alignItems: "center",
					margin: margin || "0",
				}}
			>
				{trigger}
			</span>
		</Tooltip>
	);
};

export default KnowledgeBaseTrigger;
